import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Formik, yupToFormErrors } from "formik";
import * as Yup from "yup";
import helpers from "helpers/base";
import { useAccessTier } from "contexts/AccessTierContext";
import FreeTierModalLink from "./shared/AIForm/FreeTierModalLink";
import InContentToolFormFields from "./shared/InContent/InContentToolFormFields";
import { AI_TOOL_NAMES, AI_TOOLS_LABEL_MAP } from "./constants";

const sessionStorageKey = "inContentAskLitchartsAi";

const InContentAIForm = (props) => {
  const {
    loading,
    locked,
    onResourceChange,
    onSubmit,
    options = [],
    questionMinimum,
    quotaRemaining,
    resetText,
    signupPath,
    values,
    canAskQuestions,
    toolName,
    infiniteAnswers
  } = props;

  const getValidationSchema = () => {
    if (toolName === AI_TOOL_NAMES.DISCUSSION_QUESTIONS) {
      return Yup.object().shape({
        subtopic: Yup.string().when(["title", "topic"], ([title, topic]) => {
          const selectedTitle = builtOptions.find(
            (option) => option.value === title
          );
          const selectedTopic = selectedTitle?.topics?.find(
            (t) => t.value === topic
          );
          if (selectedTopic?.data) {
            return Yup.string().required(
              `Please select your ${
                AI_TOOLS_LABEL_MAP[selectedTopic.label] ??
                selectedTopic.labelText?.toLowerCase()
              }`
            );
          }
          return Yup.string().nullable().notRequired();
        }),
        title: Yup.string().required("Please select your title"),
        topic: Yup.string().when("title", (title) => {
          if (title) {
            return Yup.string().required("Please select your topic");
          }
        })
      });
    }

    // Default: AskAI
    return Yup.object().shape({
      question: Yup.string()
        .required("25 characters required")
        .min(25, "25 characters required")
        .max(300, "Over max character limit"),
      title: Yup.string().required("Please select your title")
    });
  };

  const formRef = useRef(null);
  const { isSignedOut, isFree } = useAccessTier();

  const amplitudeKey = () => {
    if (isSignedOut) return "aiContentToolFreeTierSubmit";
    if (isFree && quotaRemaining <= 0) return "aiContentToolSubmitNoAnswers";
    return "AIContentToolSubmit";
  };
  const scrollAboveMobileKeyboard = () => {
    // Keeps iPhone keyboard from covering textarea.
    if (isSmallWindow() && helpers.isMobileAgent()) {
      const form = formRef.current;

      // Footer height minus a bit so we're not right up against the edge
      const footerHeight = document.querySelector(".footer").clientHeight - 10;
      window.scrollTo({
        behavior: "smooth",
        top: form.offsetTop + form.offsetHeight - footerHeight
      });
    }
  };

  const isSmallWindow = () => {
    return $(".context-switch-banner:visible").length;
  };

  const disableFields = !infiniteAnswers && !locked && quotaRemaining === 0;

  const unpackValueFromStorage = (key) => {
    const storageKey = `${sessionStorageKey}:${key}`;
    const storedValue = sessionStorage.getItem(storageKey) || "";
    sessionStorage.removeItem(storageKey);

    return storedValue;
  };

  const [initialValues] = useState(() => {
    if (disableFields) {
      return {
        question: "",
        subtopic: "",
        title: "",
        topic: ""
      };
    }

    if (toolName === AI_TOOL_NAMES.ASK_AI) {
      return {
        question: unpackValueFromStorage("question") || values.question,
        title: unpackValueFromStorage("title") || values.title
      };
    }

    if (toolName === AI_TOOL_NAMES.DISCUSSION_QUESTIONS) {
      return {
        subtopic: unpackValueFromStorage("subtopic") || values.subtopic,
        title: unpackValueFromStorage("title") || values.title,
        topic: unpackValueFromStorage("topic") || values.topic
      };
    }

    // Fallback in case other tools are added later
    return {
      question: values.question || "",
      subtopic: values.subtopic || "",
      title: values.title || "",
      topic: values.topic || ""
    };
  });

  const storeFieldForReload = (key, value) => {
    if (locked && !disableFields) {
      const storageKey = `${sessionStorageKey}:${key}`;
      sessionStorage.setItem(storageKey, value);
    }
  };

  const storeTitleOptionForReload = (label, value, title) => {
    const storageKey = `${sessionStorageKey}:title:option`;
    sessionStorage.setItem(storageKey, `${label}:${value}:${title}`);
  };

  const unpackTitleOptionFromStorage = () => {
    const storageKey = `${sessionStorageKey}:title:option`;
    const storedValue = sessionStorage.getItem(storageKey) || "";
    sessionStorage.removeItem(storageKey);
    if (storedValue === "") {
      return {};
    }

    const [_, label, value, title] = /(.*):(.*):(.*)/.exec(storedValue);
    return { label, title, value };
  };

  const buildOptions = () => {
    if (!initialValues.title) {
      return options;
    }
    if (options.length === 1 && options[0].value !== initialValues.title) {
      // The selected title does not match the page the user is currently viewing.
      // On initial page load, the option won't exist because the guide list hasn't been loaded.
      // Build the option for the book they had selected.
      return [unpackTitleOptionFromStorage(), ...options];
    }
    return options;
  };

  const builtOptions = buildOptions(initialValues, options);

  useEffect(() => {
    const selected = builtOptions.find(
      (option) => option.value === initialValues.title
    );

    if (selected) {
      // Force changing the resource title.
      onResourceChange(selected);
    }
  }, []);

  const handleSubmit = (formValues) => {
    // Show the free tier modal if the user is signed out and using Ask AI
    if (isSignedOut && toolName === AI_TOOL_NAMES.ASK_AI) {
      $("#ask-ai-free-tier .modal").modal("show");
      return;
    }
    // Cant ask questions, redirect to sales page
    if (!canAskQuestions) {
      window.location.href = signupPath;
      return;
    }

    // pass the values up to the parent to handle submission
    onSubmit(formValues);
  };

  const validateForm = async (formValues) => {
    if (!canAskQuestions && isFree) return;
    let yupFormErrors = {};

    if (!canAskQuestions) {
      return yupFormErrors;
    }

    try {
      const schema = getValidationSchema();
      await schema.validate(formValues, { abortEarly: false });
    } catch (e) {
      yupFormErrors = yupToFormErrors(e);
    }
    return yupFormErrors;
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      validate={validateForm}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <form
          ref={formRef}
          onFocus={scrollAboveMobileKeyboard}
          onSubmit={formik.handleSubmit}
          className="ai-form--container in-content"
        >
          <div className="ai-form--content !tw-overflow-y-visible">
            <div className="ai-form--body">
              {canAskQuestions && !isFree && !infiniteAnswers && (
                <div
                  className="ai-form--banner lc-banner lc-banner-information"
                  data-testid="ai-form-banner"
                >
                  {resetText}
                </div>
              )}
              {isSignedOut &&
                toolName !== AI_TOOL_NAMES.DISCUSSION_QUESTIONS && (
                  <div
                    className="ai-form--banner lc-banner lc-banner-information"
                    data-testid="ai-form-banner"
                  >
                    <FreeTierModalLink
                      amplitudeKey="aiContentToolFreeTierInfoBanner"
                      buttonText="Get 1 free answer"
                    />
                  </div>
                )}
              {isFree && toolName === AI_TOOL_NAMES.ASK_AI && (
                <div
                  className="ai-form--banner lc-banner lc-banner-information"
                  data-testid="ai-form-banner"
                >
                  {quotaRemaining} left (
                  <a
                    data-amplitude="aiContentToolFreeTierToPaid"
                    href={signupPath}
                  >
                    Get 250 answers per month)
                  </a>
                </div>
              )}
              <div className="ai-form--fields !tw-gap-3">
                <InContentToolFormFields
                  toolName={toolName}
                  quotaRemaining={quotaRemaining}
                  formik={formik}
                  loading={loading}
                  disableFields={disableFields}
                  locked={locked}
                  amplitudeKey={amplitudeKey}
                  isSignedOut={isSignedOut}
                  questionMinimum={questionMinimum}
                  options={builtOptions}
                  storeFieldForReload={storeFieldForReload}
                  storeTitleOptionForReload={storeTitleOptionForReload}
                  onResourceChange={onResourceChange}
                />
              </div>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

InContentAIForm.propTypes = {
  canAskQuestions: PropTypes.bool.isRequired,
  infiniteAnswers: PropTypes.bool,
  loading: PropTypes.bool,
  locked: PropTypes.bool,
  onResourceChange: PropTypes.func,
  onSubmit: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string
    })
  ),
  questionMinimum: PropTypes.number,
  quotaRemaining: PropTypes.number.isRequired,
  resetText: PropTypes.string,
  signupPath: PropTypes.string,
  toolName: PropTypes.string.isRequired,
  values: PropTypes.shape({
    question: PropTypes.string,
    subtopic: PropTypes.string,
    title: PropTypes.string,
    topic: PropTypes.string
  })
};

InContentAIForm.defaultProps = {
  infiniteAnswers: false,
  loading: false,
  locked: false,
  onResourceChange: () => {},
  onSubmit: () => {},
  options: [],
  questionMinimum: 25,
  resetText: "",
  signupPath: "/sign-up?focus=teacher",
  values: {}
};

export default InContentAIForm;
